import { Widget, ActionButton, ICONS, ToasterMode } from '@creditsights/styleguide-web';
import { Table } from 'semantic-ui-react';
import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames/bind';
import fileDownload from 'js-file-download';
import styles from './DealReportWidget.module.scss';
import { WIDGET_ID } from '../../../pages/Article/Article.constants';
import { GENERIC_ERROR_TEXT } from '../../../Base.constants';
import { useFetchDealReport, useFetchDownloadDealReport } from '../../../hooks/dealReport';
import { useToast } from '../../../hooks/toast/useToast';
import { trackDAAGReportDownload } from '../../../utils/trackUsageUtils';

const cx = classNames.bind(styles);

const DealReportWidget = (): React.ReactElement | null => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const { articleId } = useParams<string>();
  // to get the id of Deal Report in url after articles/ you substact 20 million from the Article ID
  const reportId = Number(articleId) - 20000000;

  const { showToast } = useToast();
  const { data, isLoading, isError } = useFetchDealReport(reportId);
  const { mutateAsync: downloadDealReport } = useFetchDownloadDealReport();

  const sections = data?.sections;

  if (!sections) {
    return null;
  }

  const onDownload = async (): Promise<void> => {
    setIsDownloading(true);
    try {
      const response = await downloadDealReport(reportId);
      await fileDownload(response, `Deal-at-a-glance-${reportId}.xlsx`);
      trackDAAGReportDownload(reportId);
    } catch (error) {
      showToast(GENERIC_ERROR_TEXT, ToasterMode.ERROR);
    } finally {
      setIsDownloading(false);
    }
  };

  const getClassname = (value, label) => {
    if (label === 'J.Crew Blocker' && value === 'No') {
      return 'report-red-value';
    }
    if (label !== 'J.Crew Blocker' && value === 'Yes') {
      return 'report-red-value';
    }
    return 'report-green-value';
  };

  const renderDealReport = (): React.ReactNode | null => {
    const section = sections?.find((section) => section.label === 'Loopholes & Blockers');

    if (!section) {
      return null;
    }

    const renderValue = (value, label): React.ReactNode => {
      const formatedValue = value === null ? '--' : value;
      return <div className={cx(getClassname(value, label))}>{formatedValue}</div>;
    };

    return (
      <div className={cx('report-container')}>
        <div className={cx('report-info')}>
          Our Deal at a Glance provides a quick one page summary of key documentation and covenant information from our
          new issue reports including: Doc Scores, financial tests, key baskets, debt incurrence capacities and
          loopholes.
        </div>
        <div className={cx('report-part')}>
          <Table data-testid="deal-report-table" className={cx('report-table')} basic>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell className={cx('report-first-column')}>Loopholes & Blockers</Table.HeaderCell>
                <Table.HeaderCell>Current Deal</Table.HeaderCell>
                <Table.HeaderCell>Market Comps</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {section.section_items.map((sectionItem) => {
                return (
                  <Table.Row>
                    <Table.Cell className={cx('report-first-column')}>{sectionItem.label}</Table.Cell>
                    <Table.Cell>{renderValue(sectionItem.value, sectionItem.label)}</Table.Cell>
                    <Table.Cell>{sectionItem.cvalue}</Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </div>
        <ActionButton
          icon={ICONS.Xlsx}
          onClick={onDownload}
          isLoading={isDownloading}
          tooltip="Download"
          ariaLabel="Download"
          dataTestId="deal-report-download-button"
          size={30}
        >
          Download Deal at a Glance
        </ActionButton>
      </div>
    );
  };

  if (isError) {
    return null;
  }

  return (
    <div className={cx('body')} data-testid={WIDGET_ID.DEAL_REPORT}>
      <Widget
        headerTitle={<div className={cx('heading')}>Deal at a Glance</div>}
        isLoading={isLoading}
        fixedHeight
        dataTestId="deal-report"
      >
        <div ref={containerRef} data-testid="deal-report-container">
          {renderDealReport()}
        </div>
      </Widget>
    </div>
  );
};

export default DealReportWidget;
